import _ from 'lodash';
import { LimitationData, Limitation } from 'core/limitation/Limitation';
import {
  AdministrativeArea,
  ReverseGeocodeFn
} from 'hooks/GoogleMapsPlatform/useGeocoder';
import { googleMapsUsageTracker } from 'core/googleMapsUsage/GoogleMapsUsageTracker';

const PIN_LIMITATION_TYPES = new Set(['geofence', 'customGeofence']);

type GeofencePoint = {
  lat: number;
  lng: number;
};

const geocodeCache = new Map<string, AdministrativeArea>();

function cacheKey(point: GeofencePoint): string {
  return `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
}

function collectGeofencePoints(limitations: LimitationData): GeofencePoint[] {
  // Only INCLUDE pins drive the administrativeArea projection (§9.3a). EXCLUDE
  // pins are scoped by the geofence row itself; folding them into the include
  // administrativeArea row would broaden the BE's coarse-filter scope to areas
  // the user explicitly excluded.
  const rows: Limitation[] = (limitations.include ?? []).filter((l) =>
    PIN_LIMITATION_TYPES.has(l.type)
  );

  const seen = new Set<string>();
  const points: GeofencePoint[] = [];
  for (const row of rows) {
    if (!Array.isArray(row.value)) continue;
    for (const item of row.value) {
      const [latStr, lngStr] = String(item.value).replace(/\s/g, '').split(',');
      const lat = Number.parseFloat(latStr);
      const lng = Number.parseFloat(lngStr);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
      const key = cacheKey({ lat, lng });
      if (seen.has(key)) continue;
      seen.add(key);
      points.push({ lat, lng });
    }
  }
  return points;
}

function buildAdministrativeAreaOption(
  area: AdministrativeArea
): SelectOptions {
  const value = `${area.municipality}/${area.district}`;
  return { label: value, value };
}

function stripDerivedAdministrativeArea(include: Limitation[]): Limitation[] {
  return include.filter((l) => l.type !== 'administrativeArea');
}

function writeAdministrativeAreaRow(
  include: Limitation[],
  derived: SelectOptions[]
): Limitation[] {
  const cleaned = stripDerivedAdministrativeArea(include);
  if (derived.length === 0) return cleaned;
  return [
    ...cleaned,
    {
      op: 'inc',
      type: 'administrativeArea',
      value: derived
    }
  ];
}

async function geocodeWithCache(
  point: GeofencePoint,
  geocode: ReverseGeocodeFn
): Promise<AdministrativeArea> {
  const key = cacheKey(point);
  const cached = geocodeCache.get(key);
  if (cached) return cached;
  const area = await geocode(point.lat, point.lng);
  geocodeCache.set(key, area);
  return area;
}

export type DerivationResult = {
  limitations: LimitationData;
  failedCoordinates: GeofencePoint[];
};

/**
 * For MOVABLE_OUTDOOR campaigns: recompute the administrativeArea union from
 * the current geofence/customGeofence pin set. Replaces any pre-existing
 * administrativeArea row (write-only projection — see RKB-2541 §9.3a).
 */
export async function deriveAdministrativeAreaFromGeofences(
  limitations: LimitationData,
  geocode: ReverseGeocodeFn
): Promise<DerivationResult> {
  const points = collectGeofencePoints(limitations);
  if (points.length === 0) {
    return {
      limitations: {
        ...limitations,
        include: stripDerivedAdministrativeArea(limitations.include ?? [])
      },
      failedCoordinates: []
    };
  }

  const settled = await Promise.allSettled(
    points.map((point) => geocodeWithCache(point, geocode))
  );

  const derived: SelectOptions[] = [];
  const failedCoordinates: GeofencePoint[] = [];
  settled.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      derived.push(buildAdministrativeAreaOption(result.value));
    } else {
      failedCoordinates.push(points[index]);
    }
  });

  const uniqueDerived = _.uniqBy(derived, 'value');
  const nextInclude = writeAdministrativeAreaRow(
    limitations.include ?? [],
    uniqueDerived
  );

  // MOVABLE_OUTDOOR runs its geocoder batch at pre-submit. The binder that
  // owns the reverseGeocode fn intentionally stays mounted past this point
  // (see DynamicMovableOutdoorProviders doc comment), so unmount isn't a
  // reliable flush trigger. Instead flush right after the batch completes —
  // best-effort, doesn't block the returned DerivationResult.
  void googleMapsUsageTracker.flush();

  return {
    limitations: { ...limitations, include: nextInclude },
    failedCoordinates
  };
}

/**
 * For MOVABLE_OUTDOOR campaigns on edit-load: strip the BE-derived
 * administrativeArea projection so the editor only renders user-owned state
 * (geofence / customGeofence pins). See RKB-2541 §9.3a load-time filter.
 */
export function stripDerivedAdministrativeAreaFromLimitations(
  limitations: LimitationData
): LimitationData {
  return {
    ...limitations,
    include: stripDerivedAdministrativeArea(limitations.include ?? [])
  };
}
